import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { CopyDetectionResultRecord } from '../copy-detection-result.js';
import { Exam } from '../exam.js';

/**
 * Fetches the stored copy-detection result for an exam, for the Summary canvas's copy-check tab.
 *
 * Returns `null` — not an error — when nothing has been computed yet (grading still running, or the job
 * skipped a fewer-than-2-submissions exam), so the UI can show an honest "not available yet" state.
 */
export const getCopyDetectionResultQuery = registerQuery({
  operationName: 'getCopyDetectionResultQuery',
  type: () => CopyDetectionResultRecord,
  params: [{ name: 'examId', type: () => String }],

  resolve:
    (examId: string) =>
    async (_selection: BaseQLSelectionSet<CopyDetectionResultRecord>): Promise<CopyDetectionResultRecord | null> => {
      const trimmedExamId = typeof examId === 'string' ? examId.trim() : '';
      if (!trimmedExamId) throw new Error('Klausur nicht gefunden');

      const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
      if (!currentUser?._id) throw new Error('Nicht autorisiert: Kein User eingeloggt');

      const examCollection = await getCollection(Exam as any);
      const exam = await examCollection.findOne({ _id: trimmedExamId } as any);
      if (!exam) throw new Error('Klausur nicht gefunden');
      // Same ownership check as the release/distribution queries: `publishFilter` already scopes reads to the
      // caller, but a copy-check result is sensitive enough (accusatory, about specific students) to state the
      // check explicitly rather than rely on that alone.
      if ((exam as any).userID !== currentUser._id) {
        throw new Error('Nicht autorisiert: Diese Klausur gehört dir nicht');
      }

      const resultCollection = await getCollection(CopyDetectionResultRecord);
      const record = await resultCollection.findOne({ examId: trimmedExamId });
      return record as CopyDetectionResultRecord | null;
    },
});
